export const TARGET_SAMPLE_RATE = 16000;

// Downsamples the mic to 16 kHz mono Int16 PCM and posts ~50 ms frames.
const WORKLET_SOURCE = `
class PcmDownsampler extends AudioWorkletProcessor {
  constructor(options) {
    super();
    this.ratio = sampleRate / options.processorOptions.targetRate;
    this.frameSize = options.processorOptions.frameSize;
    this.buffer = new Int16Array(this.frameSize);
    this.filled = 0;
    this.phase = 0;
    this.acc = 0;
    this.count = 0;
    this.port.onmessage = () => {
      this.port.postMessage({ last: this.buffer.slice(0, this.filled).buffer });
      this.filled = 0;
    };
  }
  process(inputs) {
    const input = inputs[0] && inputs[0][0];
    if (!input) return true;
    for (let i = 0; i < input.length; i++) {
      // Box-filter each output sample over the input samples it spans (cheap anti-aliasing).
      this.acc += input[i];
      this.count++;
      this.phase += 1;
      if (this.phase < this.ratio) continue;
      this.phase -= this.ratio;
      const s = Math.max(-1, Math.min(1, this.acc / this.count));
      this.acc = 0;
      this.count = 0;
      this.buffer[this.filled++] = s < 0 ? s * 0x8000 : s * 0x7fff;
      if (this.filled === this.frameSize) {
        this.port.postMessage(this.buffer.slice().buffer);
        this.filled = 0;
      }
    }
    return true;
  }
}
registerProcessor("pcm-downsampler", PcmDownsampler);
`;

let workletUrl: string | null = null;

export interface MicCapture {
  /** Receives every PCM frame; frames captured before this is set are replayed first. */
  setSink(sink: (frame: ArrayBuffer) => void): void;
  /** 0..1 loudness of the latest audio, for the waveform. */
  getLevel(): number;
  /** Flushes the partial frame to the sink, then releases the mic. */
  stop(): Promise<void>;
}

export async function startMicCapture(): Promise<MicCapture> {
  const stream = await navigator.mediaDevices.getUserMedia({
    audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: true },
  });
  let context = new AudioContext({ sampleRate: TARGET_SAMPLE_RATE });
  let source: MediaStreamAudioSourceNode;
  try {
    source = context.createMediaStreamSource(stream);
  } catch {
    // Firefox can't resample a mic into a 16 kHz context; the worklet downsamples instead.
    void context.close();
    context = new AudioContext();
    source = context.createMediaStreamSource(stream);
  }
  try {
    // Created after an await, outside the click; Safari can start it suspended.
    await context.resume();
    workletUrl ??= URL.createObjectURL(new Blob([WORKLET_SOURCE], { type: "application/javascript" }));
    await context.audioWorklet.addModule(workletUrl);
  } catch (err) {
    for (const track of stream.getTracks()) track.stop();
    void context.close();
    throw err;
  }

  const analyser = context.createAnalyser();
  analyser.fftSize = 512;
  const samples = new Float32Array(analyser.fftSize);
  const worklet = new AudioWorkletNode(context, "pcm-downsampler", {
    processorOptions: { targetRate: TARGET_SAMPLE_RATE, frameSize: TARGET_SAMPLE_RATE / 20 },
  });
  source.connect(analyser);
  source.connect(worklet);

  const pending: ArrayBuffer[] = [];
  let stopped = false;
  let sink: ((frame: ArrayBuffer) => void) | null = null;
  worklet.port.onmessage = (event: MessageEvent<ArrayBuffer>) => {
    if (sink) sink(event.data);
    else pending.push(event.data);
  };

  return {
    setSink(next) {
      sink = next;
      for (const frame of pending.splice(0)) next(frame);
    },
    getLevel() {
      analyser.getFloatTimeDomainData(samples);
      let sum = 0;
      for (let i = 0; i < samples.length; i++) sum += samples[i] * samples[i];
      // Speech RMS sits around 0.02–0.2; the sqrt curve lifts quiet voices into view.
      return Math.min(1, Math.sqrt(Math.sqrt(sum / samples.length) * 6));
    },
    async stop() {
      if (stopped) return;
      stopped = true;
      await new Promise<void>((resolve) => {
        const timer = setTimeout(resolve, 100);
        worklet.port.onmessage = (event: MessageEvent<ArrayBuffer | { last: ArrayBuffer }>) => {
          const frame = event.data instanceof ArrayBuffer ? event.data : event.data.last;
          if (sink && frame.byteLength) sink(frame);
          if (event.data instanceof ArrayBuffer) return;
          clearTimeout(timer);
          resolve();
        };
        worklet.port.postMessage("flush");
      });
      source.disconnect();
      worklet.disconnect();
      for (const track of stream.getTracks()) track.stop();
      await context.close();
    },
  };
}
